import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@backstage/core-plugin-api';
import { Box, Button, Paper, TextField, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { keycloakAuthApiRef } from '../../apis';
import { useRegistryAccess } from '../portal-access/useRegistryAccess';
import { ConnectionResult, ConnectionTest, RegistryApiError, SERVICE_TYPES, SourceConnReq, lhRegistryApiRef } from './api';
import { ConnectionMappings } from './ConnectionMappings';
import { Step, TICKET } from './ui';

const NEW_DOMAIN = '__new__';

/**
 * connection 등록 + 매핑 관리: 접속 정보를 OpenMetadata Database Service 로 올린다(비밀번호는 서버를 거쳐 OM 에만 간다).
 * 서비스 이름은 레지스트리의 '원천 시스템' 값이다 (db_ 접두어 없이). domain 은 기존 것을 고르거나 여기서 새로 만든다.
 */
export const RegistryNewConnection = () => {
  const api = useApi(lhRegistryApiRef);
  const keycloak = useApi(keycloakAuthApiRef);
  const { canAdmin: isAdmin } = useRegistryAccess();
  const navigate = useNavigate();
  const [domains, setDomains] = useState<string[]>([]);
  const [domain, setDomain] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [ticketKey, setTicketKey] = useState('');
  const [cf, setCf] = useState({ serviceType: 'Postgres' as SourceConnReq['serviceType'], host: '', port: '', database: '', username: '', password: '' });
  const [test, setTest] = useState<ConnectionTest>();
  const [testing, setTesting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ code: string; message: string }>();
  const [created, setCreated] = useState<ConnectionResult>();
  // 등록이 성공할 때마다 올려 매핑 표를 다시 읽게 한다 (ConnectionMappings 가 이 값이 바뀌면 목록을 다시 받는다)
  const [mapGen, setMapGen] = useState(0);

  useEffect(() => {
    api.listDomains().then(d => setDomains(d.map(x => x.domain))).catch(e => setError({ code: '', message: e.message }));
  }, [api]);

  const setC = (k: keyof typeof cf) => (e: { target: { value: string } }) => { setCf(p => ({ ...p, [k]: e.target.value })); setTest(undefined); };
  const connReq = (): SourceConnReq => ({ ...cf, port: Number(cf.port) });
  // 토큰 발급 실패는 막지 않는다 — 권한 판단은 서버에 있다(user 모드면 401 OM_TOKEN_REQUIRED, app 모드면 토큰이 필요 없다)
  const omToken = useCallback(async () => {
    try {
      return (await keycloak.getAccessToken()) || undefined;
    } catch {
      return undefined;
    }
  }, [keycloak]);

  const connReady = !!cf.host.trim() && /^\d+$/.test(cf.port) && !!cf.database.trim() && !!cf.username.trim() && !!cf.password;
  const name = serviceName.trim();
  const ticketBad = !!ticketKey && !TICKET.test(ticketKey);
  const isNew = domain === NEW_DOMAIN;
  const nd = newDomain.trim();
  // lh_domain 은 대소문자 무시로 유일하다(서버 uq_lh_domain_lower)
  const ndExists = domains.some(d => d.toLowerCase() === nd.toLowerCase());
  let ndHint: string | undefined;
  if (ndExists) ndHint = '이미 등록된 domain 입니다';
  else if (/\s/.test(nd)) ndHint = '공백은 쓸 수 없습니다';
  const domainReady = isNew ? !!nd && !/\s/.test(nd) && !ndExists : !!domain;
  const canSave = isAdmin && !busy && connReady && domainReady && !!name && !/\s/.test(name) && TICKET.test(ticketKey);

  const runTestConn = async () => {
    setTesting(true);
    setError(undefined);
    try {
      setTest(await api.testConnection(connReq()));
    } catch (e) {
      setError({ code: '', message: (e as Error).message });
    } finally {
      setTesting(false);
    }
  };

  // test 실패는 경고만 하고 등록은 진행된다(서버 동작)
  const create = async () => {
    setBusy(true);
    setError(undefined);
    try {
      let dom = domain;
      if (isNew) {
        // 새 domain 이 먼저 — 실패하면 connection 은 만들지 않는다. 성공한 뒤 connection 이 실패해도 다시 눌렀을 때 domain 을 또 만들지 않게 선택을 옮긴다
        await api.addDomain({ domain: nd, ticketKey });
        dom = nd;
        setDomains(p => [...p, nd]);
        setDomain(nd);
      }
      setCreated(await api.createConnection({ ...connReq(), domain: dom, serviceName: name, ticketKey }, await omToken()));
      setMapGen(g => g + 1);
      setCf(p => ({ ...p, password: '' }));
    } catch (e) {
      const err = e as RegistryApiError;
      setError({ code: err.code ?? '', message: err.message });
    } finally {
      setBusy(false);
    }
  };

  const mappings = <ConnectionMappings reload={mapGen} domains={domains} isAdmin={isAdmin} omToken={omToken} />;
  if (created)
    return (
      <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      <Paper variant="outlined">
        <Box p={3} display="flex" flexDirection="column" style={{ gap: 8 }}>
          <Alert severity="success">
            OpenMetadata 서비스 <b>{created.serviceName}</b> {created.omState === 'created' ? '를 등록했습니다.' : '는 이미 있어 그대로 두었습니다.'} Airflow 에는 daily 동기화 때 반영됩니다(반영 대기).
          </Alert>
          {!created.test.ok && <Alert severity="warning">접속 test 는 실패했습니다 — {created.test.message}. 등록은 진행됐고 결과는 이력에 남았습니다.</Alert>}
          {cf.serviceType === 'MongoDB' && <Alert severity="warning">MongoDB 는 Airflow 에 반영되지 않습니다.</Alert>}
          {(created.mapping?.conflictWith?.length ?? 0) > 0 && <Alert severity="warning">기본 conn_id <b>{created.mapping?.connId}</b> 가 {created.mapping?.conflictWith?.join(', ')} 와(과) 겹칩니다. 아래 매핑에서 conn_id 를 바꿔 해소하세요.</Alert>}
          {created.mappingDomainMismatch && <Alert severity="info">이 서비스는 이미 다른 domain({created.mapping?.domain ?? '미지정'})으로 매핑돼 있어 그대로 두었습니다. 바꾸려면 아래 매핑에서 편집하세요.</Alert>}
          {created.mapping === null && <Alert severity="warning">기본 conn_id 가 다른 서비스와 겹쳐 연결하지 않았습니다. 아래 매핑에서 직접 지정하세요.</Alert>}
          <Box display="flex" style={{ gap: 8 }}>
            <Button variant="contained" color="primary" onClick={() => navigate('../new', { relative: 'path' })}>테이블 등록으로</Button>
            <Button variant="outlined" onClick={() => navigate('..', { relative: 'path' })}>목록으로</Button>
          </Box>
        </Box>
      </Paper>
      {mappings}
      </Box>
    );

  const field = { variant: 'outlined' as const, size: 'small' as const };
  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
    <Paper variant="outlined" component="section" aria-label="신규 connection 등록">
      <Box p={3} display="flex" flexDirection="column" style={{ gap: 16 }}>
        <Box>
          <Typography variant="h6" component="h2">신규 connection 등록</Typography>
          <Typography variant="body2" color="textSecondary">비밀번호는 저장되지 않고 OpenMetadata 로만 전달됩니다. 이미 같은 이름의 서비스가 있으면 만들지 않습니다.</Typography>
        </Box>
        {!isAdmin && <Alert severity="info"><b>읽기 전용</b> — 쓰기 권한이 없어 등록할 수 없습니다.</Alert>}
        {error?.code === 'OM_TOKEN_REQUIRED' && <Alert severity="warning">OpenMetadata 호출에 쓸 로그인 토큰이 없습니다. 다시 로그인한 뒤 등록하세요.</Alert>}
        {error?.code === 'JIRA_UNAVAILABLE' && <Alert severity="error">Jira에 연결할 수 없어 등록을 막았습니다 (503 JIRA_UNAVAILABLE). 잠시 후 다시 시도하세요.</Alert>}
        {error && error.code !== 'JIRA_UNAVAILABLE' && error.code !== 'OM_TOKEN_REQUIRED' && <Alert severity="error">{error.message}</Alert>}

        <fieldset disabled={!isAdmin} style={{ border: 0, margin: 0, padding: 0, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Step n={1} title="서비스" sub="OpenMetadata Database Service">
            <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
              <TextField id="nc-d" {...field} select SelectProps={{ native: true }} label="domain" value={domain} onChange={e => setDomain(e.target.value)} InputLabelProps={{ shrink: true }}>
                <option value="">선택</option>
                {domains.map(d => <option key={d} value={d}>{d}</option>)}
                <option value={NEW_DOMAIN}>+ 새 domain</option>
              </TextField>
              {isNew && (
                <TextField id="nc-nd" {...field} label="새 domain 이름" placeholder="예: paymentyo" value={newDomain} onChange={e => setNewDomain(e.target.value)}
                  error={!!ndHint} helperText={ndHint ?? '추가만 됩니다 — 이름 변경·삭제는 DBA 작업입니다'} />
              )}
              <TextField id="nc-s" {...field} label="서비스 이름 (= 원천 시스템)" placeholder="예: orders_rds" value={serviceName} onChange={e => setServiceName(e.target.value)}
                error={/\s/.test(name)} helperText={/\s/.test(name) ? '공백은 쓸 수 없습니다' : undefined} />
              <TextField id="nc-t" {...field} label="근거 티켓 (필수)" placeholder="DPD-13791" value={ticketKey} onChange={e => setTicketKey(e.target.value.trim())}
                error={ticketBad} helperText={ticketBad ? '티켓 키 형식이 아닙니다' : undefined} />
            </Box>
          </Step>

          <Step n={2} title="접속 정보">
            <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
              <TextField id="nc-c0" {...field} select SelectProps={{ native: true }} label="DBMS" value={cf.serviceType} onChange={setC('serviceType')}>
                {SERVICE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </TextField>
              <TextField id="nc-c1" {...field} label="호스트" value={cf.host} onChange={setC('host')} />
              <TextField id="nc-c2" {...field} label="포트" value={cf.port} onChange={setC('port')} error={!!cf.port && !/^\d+$/.test(cf.port)} />
              <TextField id="nc-c3" {...field} label={cf.serviceType === 'Oracle' ? 'service name' : '데이터베이스'} value={cf.database} onChange={setC('database')} />
              <TextField id="nc-c4" {...field} label="계정" value={cf.username} onChange={setC('username')} autoComplete="off" />
              <TextField id="nc-c5" {...field} type="password" label="비밀번호" value={cf.password} onChange={setC('password')} autoComplete="new-password" />
            </Box>
            {cf.serviceType === 'MongoDB' && <Alert severity="warning">MongoDB 는 OpenMetadata 등록은 되지만 Airflow 에는 반영되지 않습니다.</Alert>}
            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
              <Button variant="outlined" onClick={runTestConn} disabled={!connReady || testing}>{testing ? '확인 중…' : '접속 test'}</Button>
              {test?.ok && <Typography variant="caption">✓ 접속됨 · 테이블 {test.tables.length}개</Typography>}
            </Box>
            {test && !test.ok && <Alert severity="warning">접속 test 실패 — {test.message}. 그래도 등록은 진행할 수 있습니다(실패는 이력에 남습니다).</Alert>}
          </Step>
        </fieldset>

        <Box display="flex" justifyContent="flex-end" style={{ gap: 8 }}>
          <Button variant="outlined" onClick={() => navigate('..', { relative: 'path' })}>취소</Button>
          <Button variant="contained" color="primary" onClick={create} disabled={!canSave}>connection 등록</Button>
        </Box>
      </Box>
    </Paper>
    {mappings}
    </Box>
  );
};
